/**
 * Leadler › Harita: the region map (v0.1.71). One region's scan cells drawn as their real
 * outlines (the H3 hexagons of the wave-1 grid, the boxes of the pilot's geohash cells) on a
 * Mercator projection fitted to the region, with pan and zoom (wheel, drag, pinch, and the
 * buttons for everyone else). Cells take their status colour and fade with saturation; a
 * zoomed-out view shows the companies of a cell as one bubble, a zoomed-in view as pins sized
 * by fit. Every cell and pin is a button with a name. v0.1.79: the projection can be fitted to
 * the scanned cells, scanned cells carry their scan order number and first suburb, unscanned
 * cells can fade, and the company layer can be switched off. Read only; nothing here decides.
 */

import { geoArea, geoMercator, geoPath } from 'd3-geo';
import { type KeyboardEvent, type PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  type BaseFeature,
  type BaseGeometry,
  type Basemap,
  CELL_CLASS_LABELS,
  CELL_STATUS_LABELS,
  LEAD_STAGE_LABELS,
  type LeadListItem,
  type LeadMapCell,
} from '../api/leads.js';
import { Icon } from './icons.js';

export const MAP_W = 640;
export const MAP_H = 440;
const PAD = 10;
const MIN_ZOOM = 1;
const MAX_ZOOM = 24;
/** Below this zoom the companies of a cell are one bubble; from it on they are pins. */
export const PIN_ZOOM = 2.5;
/** A region with at most this many located companies shows pins at every zoom. */
export const PIN_DIRECT_LIMIT = 30;
/** From this zoom on a cell shows its count inside the hexagon. */
const LABEL_ZOOM = 2;
const DRAG_SLOP = 4;

export interface Transform {
  k: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { k: 1, x: 0, y: 0 };

interface Projected {
  path: (cell: LeadMapCell) => string;
  point: (lat: number, lon: number) => [number, number];
  /** A basemap geometry ([lon, lat]) as an SVG path; polygon rings are wound for d3-geo. */
  geo: (geometry: BaseGeometry) => string;
}

/** A ring wound the way d3-geo wants (a ring the other way is the rest of the sphere). */
function wind(points: [number, number][]): [number, number][] {
  return geoArea({ type: 'Polygon', coordinates: [points] }) > 2 * Math.PI ? [...points].reverse() : points;
}

/** A stable key for a basemap feature: its kind of shape and its first point. */
function featureKey(feature: BaseFeature): string {
  const first =
    feature.geometry.type === 'Polygon' ? feature.geometry.coordinates[0]?.[0] : feature.geometry.coordinates[0];
  const count =
    feature.geometry.type === 'Polygon'
      ? (feature.geometry.coordinates[0]?.length ?? 0)
      : feature.geometry.coordinates.length;
  return `${feature.geometry.type}:${first?.[0] ?? 0},${first?.[1] ?? 0}:${count}`;
}

/**
 * The cell outline as a GeoJSON ring ([lon, lat]) wound the way d3-geo wants: a ring wound the
 * other way is the rest of the sphere, which paints the whole map. H3 and the mock hand out
 * counterclockwise rings, so the area check reverses them.
 */
function ring(cell: LeadMapCell): [number, number][] {
  const { minLat, minLon, maxLat, maxLon } = cell.bbox;
  const points: [number, number][] =
    cell.boundary.length >= 4
      ? cell.boundary.map(([lat, lon]) => [lon, lat])
      : [
          [minLon, minLat],
          [maxLon, minLat],
          [maxLon, maxLat],
          [minLon, maxLat],
          [minLon, minLat],
        ];
  const area = geoArea({ type: 'Polygon', coordinates: [points] });
  return area > 2 * Math.PI ? [...points].reverse() : points;
}

/** A Mercator projection fitted to the cells; null when there is nothing to fit. */
export function projectRegion(cells: readonly LeadMapCell[]): Projected | null {
  if (cells.length === 0) return null;
  const collection = {
    type: 'FeatureCollection' as const,
    features: cells.map((cell) => ({
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'Polygon' as const, coordinates: [ring(cell)] },
    })),
  };
  const projection = geoMercator().fitExtent(
    [
      [PAD, PAD],
      [MAP_W - PAD, MAP_H - PAD],
    ],
    collection,
  );
  const pathOf = geoPath(projection);
  return {
    path: (cell) => pathOf({ type: 'Polygon', coordinates: [ring(cell)] }) ?? '',
    geo: (geometry) =>
      pathOf(
        geometry.type === 'Polygon'
          ? { type: 'Polygon', coordinates: geometry.coordinates.map(wind) }
          : { type: 'LineString', coordinates: geometry.coordinates },
      ) ?? '',
    point: (lat, lon) => {
      const p = projection([lon, lat]);
      return p === null ? [Number.NaN, Number.NaN] : [p[0], p[1]];
    },
  };
}

/** The transform after zooming by `factor` about the point (px, py) in map units, clamped. */
export function zoomAbout(t: Transform, factor: number, px: number, py: number): Transform {
  const k = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, t.k * factor));
  const real = k / t.k;
  return { k, x: px - (px - t.x) * real, y: py - (py - t.y) * real };
}

/** Saturation as a share of satK: 0 when fresh, 1 when the cell has stopped giving. */
export function saturationOf(cell: Pick<LeadMapCell, 'queriesSinceLastNew' | 'satK' | 'status'>): number {
  if (cell.status === 'taranmadi' || cell.satK <= 0) return 0;
  return Math.min(1, cell.queriesSinceLastNew / cell.satK);
}

function cellLabel(cell: LeadMapCell): string {
  const where = cell.localities.length > 0 ? `${cell.localities.slice(0, 2).join(', ')}, ` : '';
  return `Hücre ${cell.cellId}, ${where}${CELL_STATUS_LABELS[cell.status]}, ${CELL_CLASS_LABELS[cell.cellClass]}, ${cell.queriesRun} sorgu, ${cell.leads} firma, ${cell.qualifiedLeads} nitelikli`;
}

function activate(event: KeyboardEvent, action: () => void): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}

/** Which map cell a company belongs to: the pilot's geohash cell or the wave-1 hexagon. */
export function cellOfCompany(company: LeadListItem, cellIds: ReadonlySet<string>): string | null {
  if (company.h3r6 !== null && cellIds.has(company.h3r6)) return company.h3r6;
  if (company.cellId !== null && cellIds.has(company.cellId)) return company.cellId;
  return null;
}

export function RegionMap({
  cells,
  companies,
  selectedCellId,
  selectedLeadId,
  onSelectCell,
  onSelectLead,
  summary,
  focus = null,
  order,
  showTowns = false,
  showCompanies = true,
  dimUnscanned = false,
  route = false,
  playhead = null,
  layers = null,
  showWater = true,
  showRoads = true,
  showIndustrial = true,
}: {
  cells: LeadMapCell[];
  /** Located companies of the region (pins and bubbles). */
  companies: LeadListItem[];
  selectedCellId: string | null;
  selectedLeadId: string | null;
  onSelectCell: (cellId: string) => void;
  onSelectLead: (leadId: string) => void;
  /** The map's accessible name. */
  summary: string;
  /** The cells the projection is fitted to (v0.1.79: the scanned ones); null fits every cell. */
  focus?: LeadMapCell[] | null;
  /** Scan order numbers drawn on the cells that have one. */
  order?: ReadonlyMap<string, number> | undefined;
  /** Draw each cell's first suburb under its centre. */
  showTowns?: boolean;
  /** Draw the company bubbles and pins. */
  showCompanies?: boolean;
  /** Fade the cells not scanned yet so the scanned corridor stands out. */
  dimUnscanned?: boolean;
  /** Draw the scan route through the ordered cells (v0.1.80). */
  route?: boolean;
  /** Playback position: cells with a higher order number show as not scanned yet, the route stops there. */
  playhead?: number | null;
  /** The basemap under the cells (v0.1.81): water and coast, main roads, industrial areas. */
  layers?: Basemap | null;
  showWater?: boolean;
  showRoads?: boolean;
  showIndustrial?: boolean;
}) {
  const fitCells = focus !== null && focus.length > 0 ? focus : cells;
  const projected = useMemo(() => projectRegion(fitCells), [fitCells]);
  const [t, setT] = useState<Transform>(IDENTITY);
  const svgRef = useRef<SVGSVGElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ x: number; y: number; moved: boolean; t: Transform } | null>(null);
  const pinch = useRef<{ distance: number; k: number } | null>(null);
  const [suppressClick, setSuppressClick] = useState(false);

  const cellIds = useMemo(() => new Set(cells.map((cell) => cell.cellId)), [cells]);
  const byCell = useMemo(() => {
    const map = new Map<string, LeadListItem[]>();
    for (const company of companies) {
      const id = cellOfCompany(company, cellIds);
      if (id === null) continue;
      const list = map.get(id) ?? [];
      list.push(company);
      map.set(id, list);
    }
    return map;
  }, [companies, cellIds]);

  const ordered = useMemo(() => {
    if (order === undefined) return [];
    return [...order.entries()]
      .sort((a, b) => a[1] - b[1])
      .flatMap(([cellId]) => cells.filter((cell) => cell.cellId === cellId));
  }, [order, cells]);

  // The wheel zooms the map, so the page must not scroll: React registers wheel listeners as
  // passive (preventDefault is ignored and the console complains); this one is not.
  useEffect(() => {
    const svg = svgRef.current;
    if (svg === null) return;
    const handler = (event: globalThis.WheelEvent) => {
      event.preventDefault();
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return;
      const scale = MAP_W / rect.width;
      const offsetY = (rect.height - MAP_H / scale) / 2;
      const px = (event.clientX - rect.left) * scale;
      const py = (event.clientY - rect.top - offsetY) * scale;
      setT((prev) => zoomAbout(prev, Math.exp(-event.deltaY * 0.0018), px, py));
    };
    svg.addEventListener('wheel', handler, { passive: false });
    return () => svg.removeEventListener('wheel', handler);
    // The svg is there from the first render whenever the region has cells (the parent keys the map by region).
  }, []);

  if (projected === null) return null;

  /** Client pixels to map units (the SVG keeps its aspect, so one ratio serves both axes). */
  const toMap = (clientX: number, clientY: number): [number, number] => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return [clientX, clientY];
    const scale = MAP_W / rect.width;
    const offsetY = (rect.height - MAP_H / scale) / 2;
    return [(clientX - rect.left) * scale, (clientY - rect.top - offsetY) * scale];
  };

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    const [px, py] = toMap(event.clientX, event.clientY);
    pointers.current.set(event.pointerId, { x: px, y: py });
    event.currentTarget.setPointerCapture(event.pointerId);
    if (pointers.current.size === 1) {
      drag.current = { x: px, y: py, moved: false, t };
      pinch.current = null;
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), k: t.k };
      drag.current = null;
    }
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    const [px, py] = toMap(event.clientX, event.clientY);
    pointers.current.set(event.pointerId, { x: px, y: py });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b) return;
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.distance === 0) return;
      const target = pinch.current.k * (distance / pinch.current.distance);
      const mid: [number, number] = [(a.x + b.x) / 2, (a.y + b.y) / 2];
      setT((prev) => zoomAbout(prev, target / prev.k, mid[0], mid[1]));
      return;
    }
    const d = drag.current;
    if (d === null) return;
    const dx = px - d.x;
    const dy = py - d.y;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    d.moved = true;
    setSuppressClick(true);
    setT({ k: d.t.k, x: d.t.x + dx, y: d.t.y + dy });
  };

  const onPointerUp = (event: PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      drag.current = null;
      // The click that ends a drag must not select a cell; the next real click may.
      setTimeout(() => setSuppressClick(false), 0);
    }
  };

  const pick = (action: () => void) => {
    if (suppressClick) return;
    action();
  };

  const zoomBy = (factor: number) => setT((prev) => zoomAbout(prev, factor, MAP_W / 2, MAP_H / 2));
  const showPins = t.k >= PIN_ZOOM || companies.length <= PIN_DIRECT_LIMIT;
  const showLabels = t.k >= LABEL_ZOOM;
  const inv = 1 / t.k;

  return (
    <div className="rm">
      <fieldset className="rm__tools">
        <legend className="sr-only">Harita denetimleri</legend>
        <button type="button" className="rm__btn" aria-label="Yakınlaştır" onClick={() => zoomBy(1.6)}>
          <Icon name="plus" size={16} />
        </button>
        <button type="button" className="rm__btn" aria-label="Uzaklaştır" onClick={() => zoomBy(1 / 1.6)}>
          <Icon name="minus" size={16} />
        </button>
        <button type="button" className="rm__btn rm__btn--text" onClick={() => setT(IDENTITY)}>
          Sığdır
        </button>
        <span className="rm__zoom mono" aria-live="polite">
          {t.k.toFixed(1)}x
        </span>
      </fieldset>
      {/* biome-ignore lint/a11y/useSemanticElements: an SVG map; the cells and pins inside are the controls. */}
      <svg
        ref={svgRef}
        className={`rm__svg${drag.current?.moved ? ' rm__svg--dragging' : ''}`}
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        role="group"
        aria-label={summary}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <g transform={`translate(${t.x} ${t.y}) scale(${t.k})`}>
          {layers === null ? null : (
            <g className="rm-base">
              {layers.layers.map((layer) => {
                const on = layer.kind === 'road' ? showRoads : layer.kind === 'industrial' ? showIndustrial : showWater;
                if (!on) return null;
                return layer.features.map((feature) => (
                  <path
                    key={`${layer.kind}:${featureKey(feature)}`}
                    className={`rm-base__${layer.kind}`}
                    d={projected.geo(feature.geometry)}
                    style={{ strokeWidth: (layer.kind === 'road' ? 1.3 : 0.9) * inv }}
                  />
                ));
              })}
            </g>
          )}
          {cells.map((cell) => {
            const on = selectedCellId === cell.cellId;
            const saturation = saturationOf(cell);
            const [cx, cy] = projected.point(cell.centroid.lat, cell.centroid.lon);
            const count = cell.qualifiedLeads > 0 ? cell.qualifiedLeads : cell.leads;
            const orderOf = order?.get(cell.cellId);
            const town = showTowns ? cell.localities[0] : undefined;
            const dim = dimUnscanned && cell.status === 'taranmadi' && !on;
            const future = playhead !== null && orderOf !== undefined && orderOf > playhead;
            return (
              <g
                key={cell.cellPk}
                className={`rm-cell rm-cell--${cell.status} rm-cell--${cell.cellClass}${on ? ' rm-cell--on' : ''}${dim ? ' rm-cell--dim' : ''}${future ? ' rm-cell--future' : ''}`}
              >
                {/* biome-ignore lint/a11y/useSemanticElements: an SVG cell; a button element cannot live inside <svg>. */}
                <path
                  className="rm-cell__shape"
                  d={projected.path(cell)}
                  style={{
                    fillOpacity: future
                      ? 0.08
                      : dim
                        ? 0.1
                        : cell.status === 'taranmadi'
                          ? 0.55
                          : 0.35 + 0.6 * saturation,
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={on}
                  aria-label={cellLabel(cell)}
                  onClick={() => pick(() => onSelectCell(cell.cellId))}
                  onKeyDown={(event) => activate(event, () => onSelectCell(cell.cellId))}
                >
                  <title>{cellLabel(cell)}</title>
                </path>
                {orderOf !== undefined && !future && Number.isFinite(cx) ? (
                  <g className="rm-order" transform={`translate(${cx} ${cy})`}>
                    <circle className="rm-order__ring" r={9 * inv} style={{ strokeWidth: 1.2 * inv }} />
                    <text className="rm-order__n" style={{ fontSize: 10 * inv }} dy={3.5 * inv}>
                      {orderOf}
                    </text>
                  </g>
                ) : showLabels && count > 0 && Number.isFinite(cx) ? (
                  <text className="rm-cell__count" x={cx} y={cy} style={{ fontSize: 11 * inv }} dy={4 * inv}>
                    {count}
                  </text>
                ) : null}
                {town !== undefined && Number.isFinite(cx) ? (
                  <text
                    className="rm-town"
                    x={cx}
                    y={cy}
                    dy={(orderOf !== undefined ? 20 : 14) * inv}
                    style={{ fontSize: 9 * inv }}
                  >
                    {town}
                  </text>
                ) : null}
              </g>
            );
          })}
          {route && ordered.length > 1 ? (
            <path
              className="rm-route"
              d={ordered
                .slice(0, playhead === null ? ordered.length : Math.max(1, playhead))
                .map((cell, index) => {
                  const [x, y] = projected.point(cell.centroid.lat, cell.centroid.lon);
                  return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
                })
                .join(' ')}
              style={{ strokeWidth: 1.6 * inv, strokeDasharray: `${5 * inv} ${4 * inv}` }}
            />
          ) : null}
          {!showCompanies
            ? null
            : showPins
              ? companies.map((company) => {
                  if (company.lat === null || company.lon === null) return null;
                  const [x, y] = projected.point(company.lat, company.lon);
                  if (!Number.isFinite(x)) return null;
                  const on = company.leadId === selectedLeadId;
                  const r = (3 + company.fit.total / 30 + (on ? 2 : 0)) * inv;
                  const label = `${company.name}, ${LEAD_STAGE_LABELS[company.stage]}, uyum ${company.fit.total}`;
                  return (
                    // biome-ignore lint/a11y/useSemanticElements: an SVG pin; a button element cannot live inside <svg>.
                    <circle
                      key={company.leadId}
                      cx={x}
                      cy={y}
                      r={r}
                      className={`rm-pin rm-pin--${company.stage}${on ? ' rm-pin--on' : ''}`}
                      style={{ strokeWidth: 1.5 * inv }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={on}
                      aria-label={label}
                      onClick={() => pick(() => onSelectLead(company.leadId))}
                      onKeyDown={(event) => activate(event, () => onSelectLead(company.leadId))}
                    >
                      <title>{label}</title>
                    </circle>
                  );
                })
              : cells.map((cell) => {
                  const inCell = byCell.get(cell.cellId) ?? [];
                  if (inCell.length === 0) return null;
                  const [cx, cy] = projected.point(cell.centroid.lat, cell.centroid.lon);
                  if (!Number.isFinite(cx)) return null;
                  const qualified = inCell.filter(
                    (company) => company.stage !== 'bulundu' && company.stage !== 'uygun_degil',
                  ).length;
                  const r = (7 + Math.min(10, Math.sqrt(inCell.length) * 2)) * inv;
                  const label = `${inCell.length} firma, ${qualified} nitelikli, hücre ${cell.cellId}`;
                  return (
                    // biome-ignore lint/a11y/useSemanticElements: an SVG bubble; a button element cannot live inside <svg>.
                    <g
                      key={`b-${cell.cellPk}`}
                      className={`rm-bubble${qualified > 0 ? ' rm-bubble--qualified' : ''}`}
                      transform={`translate(${cx} ${cy})`}
                      role="button"
                      tabIndex={0}
                      aria-label={label}
                      onClick={() => pick(() => onSelectCell(cell.cellId))}
                      onKeyDown={(event) => activate(event, () => onSelectCell(cell.cellId))}
                    >
                      <title>{label}</title>
                      <circle r={r} style={{ strokeWidth: 1.5 * inv }} />
                      <text className="rm-bubble__n" style={{ fontSize: 11 * inv }} dy={4 * inv}>
                        {inCell.length}
                      </text>
                    </g>
                  );
                })}
        </g>
      </svg>
    </div>
  );
}
